import React, { useState } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Button,
  CircularProgress,
  Chip,
} from "@mui/material";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import { COUNTRIES, getBrowserPosition, reverseGeocode } from "../utils/locationUtils";
import { useLanguage } from "../context/LanguageContext";

export interface LocationFormState {
  country_code: string;
  city: string;
  state_province: string;
  address_line1: string;
  /** Precise GPS or IP-based latitude, null when unavailable. */
  latitude: number | null;
  /** Precise GPS or IP-based longitude, null when unavailable. */
  longitude: number | null;
}

export const EMPTY_LOCATION: LocationFormState = {
  country_code: "",
  city: "",
  state_province: "",
  address_line1: "",
  latitude: null,
  longitude: null,
};

interface LocationPickerProps {
  value: LocationFormState;
  onChange: (updated: LocationFormState) => void;
  required?: boolean;
}

/**
 * Reusable location form fields for Company and Event creation.
 *
 * "Use current location" button tries GPS → reverse geocode to auto-fill all
 * fields and capture precise coordinates.  Falling back, the user fills in the
 * form manually; at minimum country is required.
 */
export default function LocationPicker({
  value,
  onChange,
  required = true,
}: LocationPickerProps) {
  const { t } = useLanguage();
  const [detecting, setDetecting] = useState(false);
  const [detectError, setDetectError] = useState(false);

  const set = (field: keyof Pick<LocationFormState, "city" | "state_province" | "address_line1">) =>
    (e: React.ChangeEvent<HTMLInputElement>) =>
      onChange({ ...value, [field]: e.target.value });

  const handleDetect = async () => {
    setDetecting(true);
    setDetectError(false);
    try {
      const pos = await getBrowserPosition();
      const geo = await reverseGeocode(pos.latitude, pos.longitude);
      onChange({
        ...value,
        country_code: geo.countryCode || value.country_code,
        city: geo.city ?? value.city,
        state_province: geo.stateProvince ?? value.state_province,
        latitude: pos.latitude,
        longitude: pos.longitude,
      });
    } catch {
      setDetectError(true);
    } finally {
      setDetecting(false);
    }
  };

  const hasCoords = value.latitude !== null && value.longitude !== null;

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {t("location.sectionLabel")}
        </Typography>

        {hasCoords ? (
          <Chip
            icon={<GpsFixedIcon sx={{ fontSize: "0.85rem !important" }} />}
            label={t("location.locationDetected")}
            size="small"
            color="success"
            variant="outlined"
            sx={{ fontSize: "0.65rem" }}
          />
        ) : (
          <Button
            size="small"
            startIcon={detecting ? <CircularProgress size={12} /> : <MyLocationIcon fontSize="small" />}
            onClick={handleDetect}
            disabled={detecting}
            sx={{ fontSize: "0.7rem", textTransform: "none" }}
          >
            {detecting ? t("location.detectingLocation") : t("location.useCurrentLocation")}
          </Button>
        )}
      </Box>

      {detectError && (
        <Typography variant="caption" color="error" sx={{ display: "block", mb: 0.5 }}>
          {t("location.locationDetectError")}
        </Typography>
      )}

      <FormControl fullWidth margin="normal" required={required}>
        <InputLabel>{t("location.country")}</InputLabel>
        <Select
          value={value.country_code}
          label={t("location.country")}
          onChange={(e) => onChange({ ...value, country_code: e.target.value as string })}
        >
          {COUNTRIES.map((c) => (
            <MenuItem key={c.code} value={c.code}>
              {c.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        label={t("location.city")}
        fullWidth
        margin="normal"
        value={value.city}
        onChange={set("city")}
        placeholder={t("location.cityPlaceholder")}
      />

      <TextField
        label={t("location.stateProvince")}
        fullWidth
        margin="normal"
        value={value.state_province}
        onChange={set("state_province")}
        placeholder={t("location.statePlaceholder")}
      />

      <TextField
        label={t("location.address")}
        fullWidth
        margin="normal"
        value={value.address_line1}
        onChange={set("address_line1")}
        placeholder={t("location.addressPlaceholder")}
      />
    </Box>
  );
}
